import { FC } from 'react';
import { Grid } from '@mui/material';
import { IMaterial } from 'src/types';
import { useAuth } from 'src/contexts';
import { uploadFile } from 'src/utils';
import { FileDetails, FileUploader } from 'src/generic/component/file';
import { FieldRow, FieldLabel, Panel, Title, RadioLabel } from 'src/generic/component';
import { BooleanRadio, TextArea, Input } from 'src/generic/component/form';
import { IFieldErrors, ErrorManager } from 'src/types/validation';
import { Draft } from 'src/types/common';
import { IAIRequest, IAIAppForm, IAccreditation } from 'src/types/org/ai/schoolInterface';

interface IAccreditationPanelProps {
  item: Draft<IAIRequest>;
  readOnly?: boolean;
  errors: IFieldErrors;
  onUpdate: (updates: Draft<IAIRequest>) => void;
}

export const AccreditationPanel: FC<IAccreditationPanelProps> = ({
  item,
  readOnly,
  errors,
  onUpdate
}) => {
  const { userName } = useAuth();
  const { getError, hasError } = ErrorManager(errors);

  const categoryId = item?.categoryId || '';

  const appForm = item.data?.appForm;

  // Principal fields from appForm
  const {
    principalName,
    principalEmail,
    principalWebpage
  } = appForm || {};

  // Accreditation fields from appForm.accreditation
  const {
    hasAccreditation,
    accreditationDetails
  } = appForm?.accreditation || {};

  const materials: IMaterial[] = appForm?.accreditation?.materials || [];

  const supportRemove = (fileName: string) => {
    const updated = [...materials];
    for (let x = 0; x < updated.length; x++) {
      if (updated[x].fileName === fileName) {
        updated.splice(x, 1);
      }
    }
    return updated;
  };

  const supportAdd = (item: IMaterial) => {
    const updated = supportRemove(item.fileName);
    updated.push(item);
    return updated;
  };

  // Function to update appForm fields
  const changedAppForm = (updates: Partial<IAIAppForm>) => {
    const updatedItem: Draft<IAIRequest> = {
      ...item,
      data: {
        ...item.data,
        appForm: {
          ...appForm,
          ...updates
        }
      }
    };
    onUpdate(updatedItem);
  };

  // Function to update accreditation fields
  const changedAccreditation = (updates: Partial<IAccreditation>) => {
    const updatedItem: Draft<IAIRequest> = {
      ...item,
      data: {
        ...item.data,
        appForm: {
          ...appForm,
          accreditation: {
            ...appForm?.accreditation,
            ...updates
          }
        }
      }
    };
    onUpdate(updatedItem);
  };

  return (
    <Panel>
      <Title>Organization Administration and Accreditation</Title>
      <Grid container spacing={5}>
        {/* Left Side Grid */}
        <Grid item xs={6}>
          {/* Principal Name Field */}
          <FieldRow>
            <FieldLabel variant="body1">
              Please provide the name of the principal or head of school for your organization
              <br />
              Please Note: The heads of school or district are the only authorized submitters of this form. Requests submitted by unauthorized users cannot be processed.
              <br />
            </FieldLabel>
            <Input
              disabled={readOnly}
              value={principalName || ''}
              validation={hasError('data.appForm.principalName') ? 'error' : undefined}
              validationMsg={getError('data.appForm.principalName')}
              onChange={(e) => {
                changedAppForm({ principalName: e.target.value });
              }}
            />
          </FieldRow>

          {/* Principal Email Field */}
          <FieldRow>
            <FieldLabel variant="body1">Please provide the professional email address for the principal or head of school for your organization</FieldLabel>
            <Input
              disabled={readOnly}
              value={principalEmail || ''}
              validation={hasError('data.appForm.principalEmail') ? 'error' : undefined}
              validationMsg={getError('data.appForm.principalEmail')}
              onChange={(e) => {
                changedAppForm({ principalEmail: e.target.value });
              }}
            />
          </FieldRow>

          {/* Principal Webpage Field */}
          <FieldRow>
            <FieldLabel variant="body1">Please provide the webpage (URL) that lists the principal or head of school for your organization</FieldLabel>
            <Input
              disabled={readOnly}
              value={principalWebpage || ''}
              validation={hasError('data.appForm.principalWebpage') ? 'error' : undefined}
              validationMsg={getError('data.appForm.principalWebpage')}
              onChange={(e) => {
                changedAppForm({ principalWebpage: e.target.value });
              }}
            />
          </FieldRow>
          
        </Grid>

        {/* Right Side Grid */}
        <Grid item xs={6}>
          {/* Accreditation Question */}
          <FieldRow>
            <RadioLabel variant="body1">
              Does your school hold accreditation from an accreditation agency, or is your school registered with the Ministry of Education?
            </RadioLabel>
            <BooleanRadio
              readOnly={readOnly}
              error={getError('data.appForm.accreditation.hasAccreditation')}
              value={hasAccreditation}
              name='hasAccreditation'
              onChange={(value) => {
                changedAccreditation({
                  hasAccreditation: value,
                  accreditationDetails: value ? accreditationDetails : undefined
                });
              }}
            />
          </FieldRow>

          {/* Conditional Accreditation Details */}
          {hasAccreditation && (
            <FieldRow>
              <FieldLabel variant="body1">
                Please list all agencies with which your organization holds full accreditation or is registered
              </FieldLabel>
              <TextArea
                disabled={readOnly}
                validation={hasError('data.appForm.accreditation.accreditationDetails') ? 'error' : undefined}
                validationMsg={getError('data.appForm.accreditation.accreditationDetails')}
                onChange={(e) => {
                  changedAccreditation({ accreditationDetails: e.target.value });
                }}
                value={accreditationDetails || ''}
              />
            </FieldRow>
          )}
          <FieldRow>
            <FieldLabel variant="body1">
              Please upload your organization's accreditation and/or Ministry of Education registration documentation (and certified English translation, if applicable), and any additional information or documentation that may be helpful in the review of your organization. Please be sure to add a description to complete the upload. Acceptable file formats are jpg, png and pdf with a max file size of 5MB each.
            </FieldLabel>
            {hasError('data.appForm.accreditation.materials') && (
              <span style={{ marginBottom: '8px' }} className='cb-input-helper cb-validation-error'>
                {getError('data.appForm.accreditation.materials')}
              </span>
            )}
          </FieldRow>

          <FieldRow>
            {materials.map((item, i) => {
              return (
                <FieldRow key={`additional-file-${i}`}>
                  <FileDetails
                    data={item}
                    readOnly={readOnly}
                    onRemove={(item) => {
                      changedAccreditation({ materials: supportRemove(item.fileName) });
                    }}
                  />
                </FieldRow>
              );
            })}
          </FieldRow>

          {!readOnly && (
            <>
              <div style={{ marginBottom: '10px', borderBottom: '1px solid #EEE' }}></div>
              <FieldRow>
                <FileUploader
                  uploadButton='Add file'
                  onUploadFile={async (file: File, description: string | undefined) => {
                    const material: IMaterial = await uploadFile(
                      userName,
                      { categoryId: categoryId },
                      file,
                      description,
                      undefined,
                      true
                    );
                    changedAccreditation({ materials: supportAdd(material) });
                  }}
                />
              </FieldRow>
            </>
          )}
        </Grid>
      </Grid>
    </Panel>
  );
};